import { Grid2X2, List, Plus, Users, Search, Edit, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Avatar from '../components/ui/Avatar';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import { currency } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { maskCep, maskCpfCnpj, maskInternationalPhone, maskPhone, maskPostalCode, maskRegistration } from '../utils/masks';
import { useLocalization } from '../hooks/useLocalization';

export default function Clientes() {
  const { settings } = useLocalization();
  const { perfil } = useAuth();
  const canViewFinance = perfil?.perfil === 'admin';
  const canManageClientes = ['admin', 'super_admin', 'gdn_admin'].includes(String(perfil?.perfil || ''));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gridView, setGridView] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [ordens, setOrdens] = useState([]);
  
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: ''
  });
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [deletingCliente, setDeletingCliente] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id
      
      if (!oficina_id) {
        setLoading(false);
        return;
      }
      
      const [clientesRes, veiculosRes, ordensRes] = await Promise.all([
        supabase.from('clientes').select('*').eq('oficina_id', oficina_id).is('removed_at', null).order('nome'),
        supabase.from('veiculos').select('id,cliente_id,marca,modelo,placa').eq('oficina_id', oficina_id).is('removed_at', null),
        supabase.from('ordens_servico').select('id,cliente_id,valor_total,status,data_entrada').eq('oficina_id', oficina_id).order('data_entrada', { ascending: false }).limit(5)
      ]);

      if (clientesRes.error) throw clientesRes.error;
      if (veiculosRes.error) throw veiculosRes.error;
      if (ordensRes.error) throw ordensRes.error;

      setClientes(clientesRes.data || []);
      setVeiculos(veiculosRes.data || []);
      setOrdens(ordensRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      if (!formData.nome.trim()) {
        toast.error('Nome do cliente é obrigatório');
        return;
      }

      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id

      if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        toast.error('Informe um email valido');
        return;
      }

      const clienteData = {
        nome: formData.nome.trim(),
        cpf: settings.countryCode === 'BR' ? formData.cpf.trim() : null,
        documento: formData.cpf.trim() || null,
        tipo_documento: settings.registrationLabel,
        pais_codigo: settings.countryCode,
        telefone: formData.telefone.trim(),
        email: formData.email.trim(),
        cep: settings.countryCode === 'BR' ? formData.cep.trim() : null,
        codigo_postal: formData.cep.trim() || null,
        rua: formData.rua.trim(),
        numero: formData.numero.trim(),
        complemento: formData.complemento.trim(),
        bairro: formData.bairro.trim(),
        cidade: formData.cidade.trim(),
        estado: formData.estado.trim(),
        oficina_id: oficina_id
      };

      let result;
      if (selectedCliente) {
        // Editar cliente existente
        result = await supabase
          .from('clientes')
          .update(clienteData)
          .eq('id', selectedCliente.id);
      } else {
        // Criar novo cliente
        result = await supabase
          .from('clientes')
          .insert(clienteData);
      }

      if (result.error) throw result.error;

      toast.success(selectedCliente ? 'Cliente atualizado com sucesso!' : 'Cliente adicionado com sucesso!');
      setShowModal(false);
      setSelectedCliente(null);
      setFormData({
        nome: '',
        cpf: '',
        telefone: '',
        email: '',
        cep: '',
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving cliente:', error);
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleEdit = (cliente) => {
    setSelectedCliente(cliente);
    setFormData({
      nome: cliente.nome || '',
      cpf: cliente.documento || cliente.cpf || '',
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      cep: cliente.codigo_postal || cliente.cep || '',
      rua: cliente.rua || '',
      numero: cliente.numero || '',
      complemento: cliente.complemento || '',
      bairro: cliente.bairro || '',
      cidade: cliente.cidade || '',
      estado: cliente.estado || ''
    });
    setShowModal(true);
  };

  const handleDelete = (cliente) => {
    if (!canManageClientes) {
      toast.error('Apenas administradores podem remover clientes.');
      return;
    }
    setClienteToDelete(cliente);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clienteToDelete || deletingCliente) return;

    try {
      setDeletingCliente(true);
      const { error } = await supabase.rpc('remove_cliente', {
        target_cliente_id: clienteToDelete.id,
      });

      if (error) throw error;

      toast.success('Cliente removido com sucesso!');
      setShowDeleteModal(false);
      setClienteToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting cliente:', error);
      toast.error(error.message || 'Erro ao remover cliente');
    } finally {
      setDeletingCliente(false);
    }
  };

  const filteredClientes = useMemo(() => {
    return (clientes || []).filter(cliente =>
      cliente.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cliente.documento || cliente.cpf)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.telefone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [clientes, searchTerm]);

  
  const getClienteStats = (clienteId) => {
    const veiculosCount = (veiculos || []).filter(v => v.cliente_id === clienteId).length;
    const totalGasto = (ordens || [])
      .filter(o => o.cliente_id === clienteId)
      .reduce((acc, ordem) => acc + Number(ordem.valor_total || 0), 0);
    const ordensCount = (ordens || []).filter(o => o.cliente_id === clienteId).length;
    
    return { veiculosCount, totalGasto, ordensCount };
  };

  const renderClienteCard = (cliente) => {
    const stats = getClienteStats(cliente.id);
    
    return (
      <Card 
        key={cliente.id} 
        className="hover:bg-surface2 cursor-pointer transition-colors"
        onClick={() => navigate(`/clientes/${cliente.id}`)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Avatar name={cliente.nome} size="md" />
            <div>
              <p className="font-semibold">{cliente.nome}</p>
              <p className="text-sm text-zinc-400">{cliente.telefone}</p>
            </div>
          </div>
          {stats.ordensCount > 3 && (
            <Badge className="bg-purple-100 text-purple-800">VIP</Badge>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-zinc-400">Veículos:</span>
            <span className="ml-2 font-medium">{stats.veiculosCount}</span>
          </div>
          <div>
            <span className="text-zinc-400">OS:</span>
            <span className="ml-2 font-medium">{stats.ordensCount}</span>
          </div>
          {canViewFinance && <div className="col-span-2">
            <span className="text-zinc-400">Total gasto:</span>
            <span className="ml-2 font-medium">{currency(stats.totalGasto)}</span>
          </div>}
        </div>
        <div className="mt-4 flex justify-end gap-2" onClick={(event) => event.stopPropagation()}>
          <Button variant="ghost" onClick={() => handleEdit(cliente)}><Edit size={14} /> Editar</Button>
          {canManageClientes && <Button variant="ghost" className="text-red-400" onClick={() => handleDelete(cliente)}><Trash2 size={14} /> Remover</Button>}
        </div>
      </Card>
    );
  };

  const renderClienteRow = (cliente) => {
    const stats = getClienteStats(cliente.id);
    
    return (
      <tr key={cliente.id} className="border-t border-border hover:bg-surface2 cursor-pointer" onClick={() => navigate(`/clientes/${cliente.id}`)}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Avatar name={cliente.nome} size="sm" />
            <div>
              <p className="font-medium">{cliente.nome}</p>
              <p className="text-sm text-zinc-400">{cliente.telefone}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">{stats.veiculosCount}</td>
        <td className="px-4 py-3">{stats.ordensCount}</td>
        {canViewFinance && <td className="px-4 py-3">{currency(stats.totalGasto)}</td>}
        <td className="px-4 py-3">
          {stats.ordensCount > 3 && (
            <Badge className="bg-purple-100 text-purple-800">VIP</Badge>
          )}
        </td>
        <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button size="sm" variant="ghost" onClick={() => handleEdit(cliente)}><Edit size={14} /></Button>
            {canManageClientes && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(cliente)}><Trash2 size={14} /></Button>}
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Carregando clientes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Clientes" 
        subtitle="Gerenciamento de clientes"
        actions={
          <div className="flex gap-2">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={16} />
              <Input
                placeholder={`Buscar por nome, ${settings.registrationLabel}, telefone ou email`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:w-64"
              />
            </div>
            <Button onClick={() => setShowModal(true)}>
              <Plus size={16} className="mr-2" />
              Novo Cliente
            </Button>
            <Button variant="ghost" onClick={() => setGridView(!gridView)}>
              {gridView ? <List size={16} /> : <Grid2X2 size={16} />}
            </Button>
          </div>
        }
      />

      {filteredClientes.length === 0 ? (
        <EmptyState
          icon={Users}
          title={searchTerm ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
          description={
            searchTerm 
              ? "Tente buscar com outro termo ou cadastre um novo cliente."
              : "Comece cadastrando seu primeiro cliente para gerenciar suas ordens de serviço."
          }
          action={() => setShowModal(true)}
          actionText="Cadastrar Cliente"
        />
      ) : (
        <>
          {gridView ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {(filteredClientes || []).map(renderClienteCard)}
            </div>
          ) : (
            <Card>
              <div className="table-scroll max-w-[calc(100vw-1.5rem)] overflow-x-auto lg:max-w-none">
              <table className="min-w-[720px] text-sm lg:w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Veículos</th>
                    <th className="text-left px-4 py-3">OS</th>
                    {canViewFinance && <th className="text-left px-4 py-3">Total Gasto</th>}
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredClientes || []).map(renderClienteRow)}
                </tbody>
              </table>
              </div>
            </Card>
          )}
        </>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Novo Cliente">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Nome *</label>
            <Input
              placeholder="Ex: Maria Silva"
              value={formData.nome}
              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
              required
            />
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">{settings.registrationLabel}</label>
              <Input
                placeholder={settings.registrationLabel}
                mask={settings.countryCode === 'BR' ? 'cpfCnpj' : 'registration'}
                value={formData.cpf}
                onChange={(e) => setFormData(prev => ({ ...prev, cpf: settings.countryCode === 'BR' ? maskCpfCnpj(e.target.value) : maskRegistration(e.target.value) }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Telefone</label>
              <Input
                placeholder={settings.countryCode === 'BR' ? '(00) 00000-0000' : '+1 555 123 4567'}
                mask={settings.countryCode === 'BR' ? 'phone' : 'internationalPhone'}
                value={formData.telefone}
                onChange={(e) => setFormData(prev => ({ ...prev, telefone: settings.countryCode === 'BR' ? maskPhone(e.target.value) : maskInternationalPhone(e.target.value) }))}
              />
            </div>
          </div>
          
          <div>
            <label className="mb-1 block text-sm font-medium">Email</label>
            <Input
              type="email"
              placeholder="cliente@email.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
          
          <div>
            <label className="mb-1 block text-sm font-medium">Rua</label>
            <Input
              placeholder="Ex: Rua das Oficinas"
              value={formData.rua}
              onChange={(e) => setFormData(prev => ({ ...prev, rua: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">{settings.postalCodeLabel}</label>
              <Input
                placeholder={settings.postalCodeLabel}
                mask={settings.countryCode === 'BR' ? 'cep' : 'postalCode'}
                value={formData.cep}
                onChange={(e) => setFormData(prev => ({ ...prev, cep: settings.countryCode === 'BR' ? maskCep(e.target.value) : maskPostalCode(e.target.value) }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Numero</label>
              <Input
                placeholder="Ex: 120"
                value={formData.numero}
                onChange={(e) => setFormData(prev => ({ ...prev, numero: e.target.value.slice(0, 20) }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Bairro</label>
              <Input
                placeholder="Ex: Centro"
                value={formData.bairro}
                onChange={(e) => setFormData(prev => ({ ...prev, bairro: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Cidade</label>
              <Input
                placeholder="Ex: Sao Paulo"
                value={formData.cidade}
                onChange={(e) => setFormData(prev => ({ ...prev, cidade: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium">{settings.regionLabel}</label>
              <Input
                placeholder="Ex: SP"
                value={formData.estado}
                onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value.slice(0, 80) }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium">Complemento</label>
              <Input
                placeholder="Ex: Sala 2, fundos, bloco B"
                value={formData.complemento}
                onChange={(e) => setFormData(prev => ({ ...prev, complemento: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Cliente
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirmar remoção">
        <div className="space-y-4">
          <p className="text-zinc-300">Deseja remover o cliente <strong>{clienteToDelete?.nome}</strong> da lista ativa?</p>
          <p className="text-sm text-zinc-500">Esta ação preserva o histórico de OS, agenda e financeiro. O cliente e seus veículos apenas deixam de aparecer nos cadastros ativos.</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" disabled={deletingCliente} onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={deletingCliente} onClick={confirmDelete}>{deletingCliente ? 'Removendo...' : 'Remover cliente'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
